import React, { useEffect, useState } from "react";
import ValetApi from "../api/Api";
import { asArray } from "../utility/asArray";
import "./AccessAssignments.css"; // <-- import the stylesheet

const AccessAssignments = ({ userId, username }) => {
    const [allLocations, setAllLocations] = useState([]);
    const [allPodiums, setAllPodiums] = useState([]);
    const [selectedLocationIds, setSelectedLocationIds] = useState([]);
    const [selectedPodiumIds, setSelectedPodiumIds] = useState([]);
    const [locationSearch, setLocationSearch] = useState("");
    const [focusedLocationId, setFocusedLocationId] = useState("");

    const locationOptions = asArray(allLocations).filter((loc) => loc.name.toLowerCase().includes(locationSearch.toLowerCase()));
    const selectedLocations = asArray(allLocations).filter((loc) => selectedLocationIds.includes(Number(loc.id)));
    const selectedLocationNameById = new Map(selectedLocations.map((loc) => [Number(loc.id), loc.name]));

    const allowedLocationIds = new Set(selectedLocationIds.map((id) => Number(id)));
    const filteredPodiums = asArray(allPodiums)
        .filter((p) => allowedLocationIds.has(Number(p.locationId)))
        .sort((a, b) => {
            const locA = selectedLocationNameById.get(Number(a.locationId)) || "";
            const locB = selectedLocationNameById.get(Number(b.locationId)) || "";
            if (locA !== locB) return locA.localeCompare(locB);
            return String(a.name || "").localeCompare(String(b.name || ""));
        });

    useEffect(() => {
        const allowedLocationIds = new Set(selectedLocationIds.map((id) => Number(id)));
        const allowed = new Set(
            asArray(allPodiums)
                .filter((p) => allowedLocationIds.has(Number(p.locationId)))
                .map((p) => Number(p.id)),
        );
        setSelectedPodiumIds((prev) => {
            const next = prev.filter((id) => allowed.has(Number(id)));
            if (next.length === prev.length && next.every((id, idx) => id === prev[idx])) {
                return prev;
            }
            return next;
        });
    }, [allPodiums, selectedLocationIds]);

    useEffect(() => {
        if (!focusedLocationId && selectedLocationIds.length) {
            setFocusedLocationId(String(selectedLocationIds[0]));
        }
    }, [selectedLocationIds, focusedLocationId]);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const [locRes, podRes, userLocRes, userPodRes] = await Promise.all([ValetApi.getAllLocations(), ValetApi.getAllPodiums(), ValetApi.getUserLocations(userId), ValetApi.getUserPodiums(userId)]);
                if (cancelled) return;
                setAllLocations(asArray(locRes?.data?.locations));
                setAllPodiums(asArray(podRes?.data?.podiums));
                const nextLocationIds = asArray(userLocRes?.data).map((l) => Number(l.locationId || l.id));
                setSelectedLocationIds(nextLocationIds);
                setSelectedPodiumIds(asArray(userPodRes?.data).map((p) => Number(p.podiumId || p.id)));
                if (nextLocationIds.length) {
                    setFocusedLocationId(String(nextLocationIds[0]));
                }
            } catch (err) {
                console.error("AccessAssignments.load error:", err);
                if (!cancelled) {
                    setAllLocations([]);
                    setAllPodiums([]);
                    setSelectedLocationIds([]);
                    setSelectedPodiumIds([]);
                    setFocusedLocationId("");
                }
            }
        }
        if (userId) load();
        return () => {
            cancelled = true;
        };
    }, [userId]);

    function toggleId(id, setter) {
        setter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    }

    function ensureLocationSelected(locationId) {
        const id = Number(locationId);
        if (!id || selectedLocationIds.includes(id)) return;
        setSelectedLocationIds((prev) => [...prev, id]);
        setLocationSearch("");
    }

    function removeLocation(locationId) {
        setSelectedLocationIds((prev) => prev.filter((id) => Number(id) !== Number(locationId)));
    }

    // async function saveLocations() {
    //     try {
    //         console.log("Saving user locations", { userId, selectedLocationIds });
    //         await ValetApi.setUserLocations(userId, selectedLocationIds);
    //         alert("Locations access updated");
    //     } catch (err) {
    //         console.error("AccessAssignments.setUserLocations error:", err);
    //         alert("Failed to update locations. See console.");
    //     }
    // }

    // async function savePodiums() {
    //     try {
    //         console.log("Saving user podiums", { userId, selectedPodiumIds });
    //         await ValetApi.setUserPodiums(userId, selectedPodiumIds);
    //         alert("Podiums access updated");
    //     } catch (err) {
    //         console.error("AccessAssignments.setUserPodiums error:", err);
    //         alert("Failed to update podiums. See console.");
    //     }
    // }
    async function saveAccess() {
        try {
            console.log("Saving user access", { userId, selectedLocationIds, selectedPodiumIds });
            await ValetApi.setUserAccess(userId, selectedLocationIds, selectedPodiumIds);
            alert("Access updated");
        } catch (err) {
            console.error("AccessAssignments.setUserAccess error:", err);
            alert(err?.[0] || "Failed to update access. See console.");
        }
    }
    return (
        <div className="admin-section access-assignments-section">
            <h3 className="access-assignments-title">Access Management for {username}</h3>

            <div className="access-assignments-grid">
                <div>
                    <h4>Locations</h4>
                    <div className="access-assignments-location-controls">
                        <input
                            type="text"
                            className="access-assignments-search"
                            placeholder="Search location..."
                            value={locationSearch}
                            onChange={(e) => {
                                const nextSearch = e.target.value;
                                setLocationSearch(nextSearch);

                                const nextOptions = asArray(allLocations).filter((loc) => loc.name.toLowerCase().includes(nextSearch.toLowerCase()));
                                const firstMatch = nextOptions[0];
                                if (firstMatch) {
                                    const firstId = Number(firstMatch.id);
                                    setFocusedLocationId(String(firstId));
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    ensureLocationSelected(focusedLocationId);
                                }
                            }}
                        />

                        <div className="access-assignments-location-row">
                            <select
                                className="access-assignments-select"
                                value={focusedLocationId}
                                onChange={(e) => {
                                    const next = e.target.value;
                                    setFocusedLocationId(next);
                                }}>
                                <option value="">Select Location</option>
                                {locationOptions.map((loc) => (
                                    <option
                                        key={loc.id}
                                        value={loc.id}>
                                        {loc.name}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="access-assignments-button access-assignments-add-button"
                                onClick={() => ensureLocationSelected(focusedLocationId)}
                                disabled={!focusedLocationId || selectedLocationIds.includes(Number(focusedLocationId))}>
                                Add Location
                            </button>
                        </div>
                        <div className="access-assignments-hint">Type to search, then click Add Location (or press Enter).</div>
                    </div>

                    <div className="access-assignments-selected-locations">
                        {selectedLocations.length === 0 && <div className="access-assignments-hint">No assigned locations yet.</div>}
                        {selectedLocations.map((loc) => (
                            <div
                                key={loc.id}
                                className="access-assignments-chip">
                                <button
                                    type="button"
                                    className="access-assignments-chip-name"
                                    onClick={() => setFocusedLocationId(String(loc.id))}>
                                    {loc.name}
                                </button>
                                <button
                                    type="button"
                                    className="access-assignments-chip-remove"
                                    onClick={() => removeLocation(Number(loc.id))}>
                                    x
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <h4>Podiums {selectedLocationIds.length ? "- All Selected Locations" : ""}</h4>
                    <div className="access-assignments-listbox">
                        {!selectedLocationIds.length && <div className="access-assignments-hint">Select at least one location to manage podium access.</div>}
                        {asArray(filteredPodiums).map((p) => {
                            const id = Number(p.id);
                            return (
                                <label
                                    key={id}
                                    className="access-assignments-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={selectedPodiumIds.includes(id)}
                                        onChange={() => toggleId(id, setSelectedPodiumIds)}
                                    />
                                    <span>
                                        {p.name}
                                        {selectedLocationNameById.get(Number(p.locationId)) ? ` (${selectedLocationNameById.get(Number(p.locationId))})` : ""}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                    <button
                        type="button"
                        className="access-assignments-button"
                        onClick={saveAccess}>
                        Save Access
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AccessAssignments;
